import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Award, Heart, Home as HomeIcon, Mountain } from "lucide-react";
import { OWNER_PHOTO } from "../data";

const VALUES = [
  { icon: Award, label: "Eagle Scout", note: "The highest rank in Scouting — earned through years of service and stewardship." },
  { icon: Heart, label: "Two-Year Volunteer Mission", note: "Served a full-time voluntary church mission." },
  { icon: HomeIcon, label: "Husband & Father of Two", note: "Building a life and a business here in the valley." },
  { icon: Mountain, label: "Born & Raised in Kalispell", note: "Montana isn't a job site to me — it's home." },
];

export default function About() {
  return (
    <main data-testid="about-page" className="pt-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 lg:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 items-start">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 relative overflow-hidden rounded-sm border border-[#D1CFC7] aspect-[4/5] lg:sticky lg:top-28"
          >
            <img
              src={OWNER_PHOTO}
              alt="Jonathan Oftedahl with his family in the Flathead Valley"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-[#1E352F]/85 to-transparent">
              <p className="font-serif-display text-2xl text-[#F8F7F4]">Jonathan Oftedahl</p>
              <p className="text-sm text-[#D9E0DA]">Owner & Builder · Kalispell, Montana</p>
            </div>
          </motion.div>

          {/* Story */}
          <div className="lg:col-span-7">
            <span className="text-xs tracking-[0.25em] uppercase font-semibold text-[#A95C42]">
              About
            </span>
            <h1 className="font-serif-display text-5xl sm:text-6xl tracking-tight font-medium text-[#1E352F] mt-2">
              Born and raised here.
            </h1>
            <div className="mt-5 space-y-4 text-base text-[#4A5D54] leading-relaxed max-w-2xl">
              <p>
                Hi, I'm Jonathan Oftedahl. I was born and raised in Kalispell,
                Montana — this is my home. I love this valley, and I've spent my
                whole life working here. Over the past 20 years I've made it my
                mission to help make this place more beautiful and to let people
                truly see and enjoy the nature all around them.
              </p>
              <p>
                I've worn a lot of hats along the way. I started out in lawn care
                and landscaping, then moved into logging and tree thinning,
                excavation, and wildland firefighting. From there I worked in dirt
                construction, house construction, and roof construction. I even
                built up and sold my own roof truss company here in the Flathead Valley.
                Every one of those jobs taught me something I still use on the land
                today.
              </p>
              <p>
                I'm also an Eagle Scout, and I served a two-year voluntary church
                mission — experiences that taught me hard work, follow-through, and
                real pride in serving others. Today I'm a husband and a father of
                two beautiful children, and I bring that same care and dedication to
                every project I take on.
              </p>
              <p>
                Whether it's a quiet path through your own woods or a bigger build
                across the property, my goal is simple: clean, lasting work that
                opens up the land you love — no trail too big, no trail too small.
              </p>
            </div>

            <div className="mt-8 rounded-sm border border-[#D1CFC7] bg-[#FFFFFF] p-6">
              <div className="flex items-baseline gap-3">
                <span className="font-serif-display text-4xl text-[#2D4A3E] leading-none">20</span>
                <span className="text-sm tracking-[0.15em] uppercase font-semibold text-[#4A5D54]">
                  Years working the Flathead Valley
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mt-5">
                {[
                  "Lawn Care",
                  "Landscaping",
                  "Logging & Tree Thinning",
                  "Excavation",
                  "Wildland Firefighting",
                  "Dirt Construction",
                  "House Construction",
                  "Roof Construction",
                  "Business Owner",
                ].map((t) => (
                  <span
                    key={t}
                    className="text-xs tracking-wide px-3 py-1.5 rounded-sm bg-[#EAE7E0] text-[#2D4A3E] border border-[#D1CFC7]"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
              {VALUES.map((v, i) => {
                const Icon = v.icon;
                return (
                  <motion.div
                    key={v.label}
                    data-testid={`about-value-${i}`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.06 }}
                    className="rounded-sm border border-[#D1CFC7] bg-[#FFFFFF] p-5 flex gap-4"
                  >
                    <Icon className="h-6 w-6 text-[#2D4A3E] shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div>
                      <p className="font-medium text-[#1E352F]">{v.label}</p>
                      <p className="text-sm text-[#4A5D54] mt-0.5 leading-relaxed">{v.note}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <Link
              to="/quote"
              data-testid="about-quote-button"
              className="mt-8 inline-flex items-center gap-2 bg-[#2D4A3E] text-[#F8F7F4] px-6 py-3 rounded-sm font-medium hover:bg-[#1E352F] transition-colors duration-300"
            >
              Let's build something <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
